import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Field, change, getFormValues, getFormAsyncErrors,
} from 'redux-form';
import { t } from 'i18next';
import { connect } from 'react-redux';
import {
  getCompanyContacts,
  getContactInfo,
  createContact,
  updateContactById,
  getContactPermissionInfo,
} from '../../../services/contacts';
import AddressComplete from '../AddressComplete';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import { phoneNumber, digitOnly } from '../../../utils/mask';
import {
  isRequired, isPhone, isEmail, maxChar40, maxChar50, maxChar5,
  maxChar30,
  maxChar2,
  isCANPostalvalidation,
  isUSZipvalidation,
  maxChar7,
  maxChar6,
} from '../../../utils/validator';
import { capitalizeFirstLetter, littleCaseFirstLetter, getCountryFromZip } from '../../../utils/func';
import {
  Container,
  LineButton,
  EditButtonGroup,
  Icon,
  SuccessMsg,
} from './css';
import {
  ContentBoxInfoColumn,
  ContentBoxSubTitle,
  ContentBoxText,
} from '../steps/ContentBox/css';
import Edit from '../../../assets/icons/Edit.svg';
import Plus from '../../../assets/icons/Plus.svg';
import PlusBlack from '../../../assets/icons/PlusBlack.svg';
import { formApiToData, formDataToApi } from '../../../services/contacts/helpers';
import { putReduxAllCompanies } from '../../ManageCompanies/actions';
import putReduxAllContacts from '../../ManageContacts/actions';
import { getCompaniesList } from '../../../services/companies';
import { AddressTypes, Divisions, DeliveryTypes } from '../../../utils/enums';
import Checkbox from '../../../lib/ui/form_elements/Checkbox';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';

const AddressBookEntryBase = (props) => {
  const {
    formName,
    userAddressType,
    changeFormValue,
    division,
    userId,
    contactList,
    companiesList,
    isAuthenticated,
    formData,
    formAsyncErrors,
    hideNickname,
    defaultContactId,
    defaultName,
    defaultPostalCode,
    isPhoneNumberRequired = true,
    validator,
    dispatch,
    noAddressSuggestion,
    showConsigneeInfoCheckbox,
  } = props;

  const contactType = props.contactType.toLowerCase();
  const isShipper = contactType === AddressTypes.SHIPPER;
  const [state, setState] = useState({
    contactSelected: false,
    contactId: '',
    contacts: [],
  });

  let { contactId } = state;
  const { contactSelected } = state;
  const fetchAllContacts = (companies) => {
    if (companies) {
      return Promise.all(companies.map((comp) => getCompanyContacts(comp.companyId))).then((contactRequests) => {
        let allContacts = [];

        contactRequests.forEach((contactRequest) => {
          if (!contactRequest.data || contactRequest.data.length === 0) {
            return;
          }

          allContacts = allContacts.concat(contactRequest.data);
        });

        setState({ ...state, contacts: allContacts });
        return allContacts;
      });
    }

    return undefined;
  };

  const initialCalls = () => {
    getCompaniesList(division, userId).then(
      (cl) => {
        const { putReduxAllCompanies, putReduxAllContacts } = props;
        putReduxAllCompanies(cl.data);
        fetchAllContacts(cl.data).then((res) => {
          const formedContactList = res;
          putReduxAllContacts(formedContactList);
        });
      },
    );
  };

  const dispatchDefaultValues = (contactType, name, postalCode) => {
    dispatch(change(formName, `${contactType}ContactName`, name));
    dispatch(change(formName, `${contactType}PostalZipCode`, postalCode));
  };

  const clearNickname = (nickname) => {
    Object.keys(formData).forEach((key) => {
      if (key.startsWith(contactType)) {
        changeFormValue({
          formName,
          fieldName: key,
          newVal: null,
        });
      }
    });
    changeFormValue({
      formName,
      fieldName: `${contactType}Nickname`,
      newVal: { name: nickname },
    });
    setState({ ...state, contactSelected: false, contactId: '' });
  };

  const contactTypeFilteredList = (currentContactList) => {
    if (contactType === 'billing') {
      return contactList;
    }

    return (currentContactList || contactList).filter((c) =>
      [contactType.toLowerCase(), AddressTypes.SHARED, AddressTypes.OTHER].includes(c.contactType.toLowerCase()),
    );
  };

  const selectedNickname = (contactId, currentContactList) => {
    if (contactId && contactId > 0) {
      const selectedContact = contactTypeFilteredList(currentContactList).find((c) => String(c.contactId) === String(contactId));
      // eslint-disable-next-line no-unused-expressions
      selectedContact ? getContactInfo(selectedContact.contactId).then((res) => {
        const formedData = formApiToData(res.data);
        Object.keys(formedData).forEach((key) => {
          changeFormValue({
            formName,
            fieldName: `${contactType}${capitalizeFirstLetter(key)}`,
            newVal: formedData[key],
          });
        });
        setState({ ...state, contactId: selectedContact.contactId });
      })
        : clearNickname(t());
    } else {
      clearNickname(t());
    }
  };

  if (isAuthenticated && !hideNickname) {
    useEffect(() => {
      // eslint-disable-next-line no-unused-expressions
      contactType.toLowerCase() === AddressTypes.SHIPPER.toLowerCase() && initialCalls();
    }, []);

    useEffect(() => {
      if (formData[`${contactType}RowVersion`] && !contactSelected) {
        setState({ ...state, contactSelected: true });
      } else if (contactSelected && !formData[`${contactType}RowVersion`]) {
        setState({ ...state, contactSelected: false });
      }
    });

    useEffect(() => {
      if (defaultContactId === 0) {
        dispatchDefaultValues(contactType, defaultName, defaultPostalCode);
      }

      if (contactList && contactList.length > 0 && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get(contactType)) {
          selectedNickname(urlParams.get(contactType));
        } else if (defaultContactId !== undefined && !formData[`${contactType}Nickname`]) {
          selectedNickname(defaultContactId);
        }
      }
    }, [contactList]);
  }

  const pushContactInfo = () => {
    const {
      formData, putReduxAllContacts, userId, openContactPermissionModal,
    } = props;
    const { contactSelected } = state;
    /*
    purpose:parse formData for all objects with keys starting with contact Type ('shipper'/'consignee')
    returns: true/false map of which keys to operate on
    */
    const contactObjectMap = Object.keys(formData).map((f) => (
      // eslint-disable-next-line no-sequences
      userId,
      division,
      f.startsWith(contactType)
    ));
    // match index of true/false map with object values array
    const contactObjectData = contactObjectMap.map((f, index) => {
      // parse object keys into right case
      const keyName = littleCaseFirstLetter(Object.keys(formData)[index].substring(contactType.length));

      // returns object values for contact type associated values, otherwise returns false
      return f && (
        {
          [keyName]:
            Object.values(formData)[index],
        }
      );
    })
      // parse out false
      .filter((f) => f)
      // rebuild object
      .reduce((curr, accum) => ({ ...curr, ...accum }), {});

    // add companyId and contact Type information
    contactObjectData.companyId = companiesList[0].companyId;
    contactObjectData.contactType = contactType;
    contactObjectData.userId = userId;
    contactObjectData.division = division;

    const formedData = formDataToApi(contactObjectData);
    if (!contactId && contactType === 'shipper') {
      contactId = formData.shipperNickname ? formData.shipperNickname.contactId : '';
    } else if (!contactId && contactType === 'consignee') {
      contactId = formData.consigneeNickname ? formData.consigneeNickname.contactId : '';
    }

    // eslint-disable-next-line no-unused-expressions
    contactSelected
      ? getContactPermissionInfo(division, userId).then((res) => {
        // eslint-disable-next-line no-unused-expressions
        (res.data.message === true)
          ? updateContactById(formedData, contactId).then((res) => {
            // eslint-disable-next-line no-unused-expressions
            res.status === 204 && fetchAllContacts(companiesList).then((res) => {
              const formedContactList = res;
              putReduxAllContacts(formedContactList);
              changeFormValue({
                formName,
                fieldName: `${contactType}NicknameSuccess`,
                newVal: (t('ContactUpdate')),
              });
              setTimeout(() => changeFormValue({
                formName,
                fieldName: `${contactType}NicknameSuccess`,
                newVal: '',
              }), 3000);
              selectedNickname(contactId, formedContactList);
            });
          }) : openContactPermissionModal();
      })
      : getContactPermissionInfo(division, userId).then((res) => {
        // eslint-disable-next-line no-unused-expressions
        (res.data.message === true)
          ? createContact(formedData).then((res) => {
            const { contactId } = res.data;
            if (res.status === 201) {
              setState({ ...state, contactSelected: true });
              fetchAllContacts(companiesList).then((res) => {
                const formedContactList = res;
                putReduxAllContacts(formedContactList);
                changeFormValue({
                  formName,
                  fieldName: `${contactType}NicknameSuccess`,
                  newVal: (t('Contact Successfully Added')),
                });
                setTimeout(() => changeFormValue({
                  formName,
                  fieldName: `${contactType}NicknameSuccess`,
                  newVal: '',
                }), 3000);
                selectedNickname(contactId, formedContactList);
              });
            }
          }) : openContactPermissionModal();
      });
  };

  const requiredMatch = contactType === userAddressType;

  const isValidSubForm = () => {
    if (
      formData[`${contactType}Nickname`]
      && ((requiredMatch && formData[`${contactType}ContactName`])
      && ((formData && !(formData.division === Divisions.Sameday.name && formData.deliveryType.name === DeliveryTypes.Residential))
      || requiredMatch) && formData[`${contactType}CompanyName`])
      && formData[`${contactType}Address`]
      && (formData[`${contactType}Email`])
      && !isEmail(formData[`${contactType}Email`])
      && formData[`${contactType}PhoneNumber`]
      && !isPhone(formData[`${contactType}PhoneNumber`])
    ) {
      return true;
    }

    return false;
  };

  const phoneValidators = isPhoneNumberRequired ? [isPhone, maxChar50, isRequired] : [isPhone, maxChar50];
  const addressValidator = () => validator(formData, dispatch, { asyncErrors: formAsyncErrors }, `${contactType}AddressLine1`, isAuthenticated);

  const nicknameFormatter = (details) => (details ? details.nickname : null);

  const fillCountryFromZip = (contactType) => {
    if (!formData[`${contactType}Country`]) {
      dispatch(change(formName, `${contactType}Country`, getCountryFromZip(formData[`${contactType}PostalZipCode`])));
    }
  };

  return (
    <Container>
      {!hideNickname && isAuthenticated && (
        <Field
          name={`${contactType}Nickname`}
          label={t('Nickname')}
          component={Dropdown}
          options={contactList && contactTypeFilteredList().map((item) => ({ ...item, value: nicknameFormatter(item) }))}
          valueField="contactId"
          valueFormatter={nicknameFormatter}
          onChange={(contactId) => contactId && selectedNickname(contactId)}
          optional
          nicknameClick={(v) => clearNickname(v)}
          nicknameInput
        />
      )}

      <Field
        name={`${contactType}ContactName`}
        label={t('ContactName')}
        component={TextInput}
        validate={[maxChar40, isRequired]}
        maxLength={40}
      />

      <Field
        name={`${contactType}CompanyName`}
        label={t('CompanyName')}
        component={TextInput}
        validate={isShipper ? [maxChar40, isRequired] : [maxChar40]}
        maxLength={40}
        optional={!isShipper}
      />

      {noAddressSuggestion ? (
        <Field
          name={`${contactType}Address`}
          label={t('FullAddress')}
          component={TextInput}
          validate={isShipper ? [maxChar50, isRequired] : [maxChar50]}
          maxLength={50}
          optional={!isShipper}
        />
      ) : (
        <AddressComplete
          contactType={contactType}
          fieldName={`${contactType}Address`}
          label={t('AddressLine1')}
          formName={formName}
          validator={addressValidator}
          logInError={formAsyncErrors}
          required={isShipper}
        />
      )}

      <Field
        name={`${contactType}AddressAdditional`}
        label={t('AddressLine2')}
        component={TextInput}
        validate={[maxChar40]}
        maxLength={40}
        optional
      />
      <Row>
        <Column>
          <Field
            name={`${contactType}City`}
            label={t('City')}
            component={TextInput}
            validate={isShipper ? [maxChar30, isRequired] : [maxChar30]}
            maxLength={30}
            disabled={formData[`is${capitalizeFirstLetter(contactType)}AddressCanadaPost`]}
            optional={!isShipper}
          />
        </Column>
        <Column>
          <Field
            name={`${contactType}ProvinceState`}
            label={t('ProvinceState')}
            component={TextInput}
            required
            validate={[maxChar2, isRequired]}
            maxLength={2}
            disabled={formData[`is${capitalizeFirstLetter(contactType)}AddressCanadaPost`]}
          />
        </Column>
      </Row>

      <Row>
        <Column>
          <Field
            name={`${contactType}PostalZipCode`}
            page={t('CreateQuote')}
            label={t('PostalZipCode')}
            component={TextInput}
            normalize={
              (formData?.[`${contactType}Country`] === 'US'
                || formData?.[`${contactType}Country`] === 'USA'
              )
                  && digitOnly
                }
            validate={[
              isRequired,

              (
                formData?.[`${contactType}Country`] === 'CA'
                || formData?.[`${contactType}Country`] === 'Canada'
              )
                ? isCANPostalvalidation
                : (
                  formData?.[`${contactType}Country`] === 'US'
                  || formData?.[`${contactType}Country`] === 'USA'
                )
                  ? isUSZipvalidation
                  : null,

              (
                formData?.[`${contactType}Country`] === 'CA'
                || formData?.[`${contactType}Country`] === 'Canada'
              )
                ? (
                  formData?.[`${contactType}PostalZipCode`]?.includes(' ')
                    ? maxChar7
                    : maxChar6
                )
                : (
                  formData?.[`${contactType}Country`] === 'US'
                  || formData?.[`${contactType}Country`] === 'USA'
                )
                  ? maxChar5
                  : null,
            ].filter(Boolean)}
            maxLength={
              (
                formData?.[`${contactType}Country`] === 'CA'
                || formData?.[`${contactType}Country`] === 'Canada'
              )
                ? (
                  formData?.[`${contactType}PostalZipCode`]?.includes(' ')
                    ? 7
                    : 6
                )
                : (
                  formData?.[`${contactType}Country`] === 'US'
                  || formData?.[`${contactType}Country`] === 'USA'
                )
                  ? 5
                  : undefined
            }
            isAuthenticated={isAuthenticated}
            disabled={formData[`is${capitalizeFirstLetter(contactType)}AddressCanadaPost`]}
            onBlur={() => fillCountryFromZip(contactType)}
          />
        </Column>
        <Column>
          <Field
            name={`${contactType}Country`}
            label={t('Country')}
            component={TextInput}
            validate={isShipper ? [maxChar6, isRequired] : [maxChar6]}
            maxLength={6}
            disabled={formData[`is${capitalizeFirstLetter(contactType)}AddressCanadaPost`]}
            optional={!isShipper}
          />
        </Column>
      </Row>

      <Field
        name={`${contactType}ResidentialAddress`}
        label={t('ResidentialAddress')}
        component={Checkbox}
      />
      <Field
        name={`${contactType}Email`}
        label={t('EmailAddress')}
        component={TextInput}
        validate={[isEmail, maxChar50].concat(contactType !== AddressTypes.CONSIGNEE ? isRequired : [])}
        optional={contactType === AddressTypes.CONSIGNEE}
        maxLength={50}
      />

      <Row>
        <Column>
          <Field
            name={`${contactType}PhoneNumber`}
            label={t('PhoneNumber')}
            component={TextInput}
            validate={phoneValidators}
            normalize={phoneNumber}
            maxLength={50}
          />
        </Column>
        <Column>
          <Field
            name={`${contactType}PhoneNumberExt`}
            label={t('Extension')}
            component={TextInput}
            validate={[maxChar5]}
            maxLength={5}
            normalize={digitOnly}
            optional
          />
        </Column>
      </Row>

      {!hideNickname && isAuthenticated && (
        <EditButtonGroup>
          {
            formData[`${contactType}NicknameSuccess`] ? (
              <SuccessMsg>{formData[`${contactType}NicknameSuccess`]}</SuccessMsg>
            ) : (
              <LineButton name={`${contactSelected ? 'editContact' : 'addContact'}`} type="button" onClick={() => pushContactInfo()}>
                <Icon
                  className="icon"
                  src={contactSelected ? Edit : (!isValidSubForm() ? PlusBlack : Plus)}
                  alt={`${contactSelected ? 'edit' : 'add'}`}
                />
                {contactSelected ? t('UpdateContact') : t('AddressBookAddControl')}
              </LineButton>
            )
          }
        </EditButtonGroup>
      )}

      {showConsigneeInfoCheckbox && (
        <Field
          name="sendConsigneeInfo"
          label={t('EmailConsigneeShipmentSummary')}
          component={Checkbox}
        />
      )}
    </Container>
  );
};

const mstp = (state) => ({
  formData: getFormValues(Object.keys(state.form)[0])(state),
  formAsyncErrors: getFormAsyncErrors(Object.keys(state.form)[0])(state),
  isAuthenticated: state.profile.isAuthenticated,
  division: state.profile.division,
  userId: state.profile.userId,
  companiesList: state.user.allCompanies,
  contactList: state.user.allContacts,
});
const mdtp = (dispatch) => ({
  putReduxAllContacts: (allContacts) => dispatch(putReduxAllContacts(allContacts)),
  putReduxAllCompanies: (allCompanies) => dispatch(putReduxAllCompanies(allCompanies)),
  changeFormValue: (load) => {
    const { formName, fieldName, newVal } = load;
    dispatch(change(formName, fieldName, newVal));
  },
  dispatch,
});

export const AddressBookEntry = connect(mstp, mdtp)(AddressBookEntryBase);

export const AddressBookText = ({ formData = {}, contactType }) => (
  <>
    {
        formData[`${contactType}ContactName`] && (
          <ContentBoxInfoColumn>
            <ContentBoxSubTitle>{t('ContactName')}</ContentBoxSubTitle>
            <ContentBoxText>{formData[`${contactType}ContactName`]}</ContentBoxText>
          </ContentBoxInfoColumn>
        )
      }

    {
        formData[`${contactType}CompanyName`] && (
          <ContentBoxInfoColumn>
            <ContentBoxSubTitle>{t('CompanyName')}</ContentBoxSubTitle>
            <ContentBoxText>{formData[`${contactType}CompanyName`]}</ContentBoxText>
          </ContentBoxInfoColumn>
        )
      }

    {
        formData[`${contactType}Address`] && (
          <ContentBoxInfoColumn>
            <ContentBoxSubTitle>{t('Address')}</ContentBoxSubTitle>
            <ContentBoxText>
              {formData[`${contactType}Address`]}
            </ContentBoxText>
          </ContentBoxInfoColumn>
        )
      }

    <ContentBoxInfoColumn>
      <ContentBoxSubTitle>{t('AddressAdditional')}</ContentBoxSubTitle>
      <ContentBoxText>{formData[`${contactType}AddressAdditional`] ? formData[`${contactType}AddressAdditional`] : '-'}</ContentBoxText>
    </ContentBoxInfoColumn>

    <ContentBoxInfoColumn>
      <ContentBoxSubTitle>{t('IsResidentialAddress')}</ContentBoxSubTitle>
      <ContentBoxText>{formData[`${contactType}ResidentialAddress`] ? t('Yes') : t('No')}</ContentBoxText>
    </ContentBoxInfoColumn>

    {
        formData[`${contactType}Email`] && (
          <ContentBoxInfoColumn>
            <ContentBoxSubTitle>{t('Email')}</ContentBoxSubTitle>
            <ContentBoxText>{formData[`${contactType}Email`]}</ContentBoxText>
          </ContentBoxInfoColumn>
        )
      }

    {
        formData[`${contactType}PhoneNumber`] && (
          <ContentBoxInfoColumn>
            <ContentBoxSubTitle>{t('PhoneNumber')}</ContentBoxSubTitle>
            <ContentBoxText>{formData[`${contactType}PhoneNumber`]}</ContentBoxText>
          </ContentBoxInfoColumn>
        )
      }

    <ContentBoxInfoColumn>
      <ContentBoxSubTitle>{t('PhoneNumberExt')}</ContentBoxSubTitle>
      <ContentBoxText>{formData[`${contactType}PhoneNumberExt`] ? formData[`${contactType}PhoneNumberExt`] : '-'}</ContentBoxText>
    </ContentBoxInfoColumn>
  </>
);

AddressBookEntryBase.propTypes = {
  formName: PropTypes.string,
  userAddressType: PropTypes.string,
  changeFormValue: PropTypes.func,
  division: PropTypes.string,
  userId: PropTypes.string,
  contactList: PropTypes.array,
  companiesList: PropTypes.array,
  isAuthenticated: PropTypes.bool,
  formData: PropTypes.object,
  formAsyncErrors: PropTypes.object,
  hideNickname: PropTypes.bool,
  defaultContactId: PropTypes.number,
  defaultName: PropTypes.string,
  defaultPostalCode: PropTypes.string,
  isPhoneNumberRequired: PropTypes.bool,
  validator: PropTypes.func,
  dispatch: PropTypes.func,
  noAddressSuggestion: PropTypes.bool,
  showConsigneeInfoCheckbox: PropTypes.bool,
  putReduxAllCompanies: PropTypes.func,
  putReduxAllContacts: PropTypes.func,
  openContactPermissionModal: PropTypes.func,
  contactType: PropTypes.string,
};

AddressBookText.propTypes = {
  formData: PropTypes.object,
  contactType: PropTypes.string,
};
